import {useState} from 'react';
import {Alert, Button, Group, Stack, Text, Title} from '@mantine/core';
import {AiOutlineInfoCircle, AiOutlinePlus} from 'react-icons/ai';

import {ApplicationInfrastructureProfile} from './ApplicationInfrastructureProfile';
import {RegisterInfrastructureForm} from '../forms/RegisterInfrastructureForm';
import {useApplicationInfrastructure} from '../hooks/useApplicationInfrastructure';
import {Application} from '../types';
import {RegisterInfrastructureFormValues} from '../types-infrastructure';

// FNP-1016: the Infrastructure tab for one application.
//
// Three states, and the ORDER of the checks is the safety property:
//
//   1. loading            → say so. Never offer to register while the answer is
//                           unknown; `records` is empty during the fetch, and
//                           an affordance shown then produces a DUPLICATE row
//                           for an application that already has one.
//   2. record exists      → DISPLAY it. No register affordance at all.
//   3. no record          → offer registration.

export const ApplicationInfrastructureSection = (
    {application}: { application: Application }) => {

    const [registering, setRegistering] = useState(false);

    const {
        record,
        hasInventory,
        loading,
        registerRecord,
    } = useApplicationInfrastructure(application.id);

    if (loading) {
        return (
            <Stack gap={'xs'}>
                <Title order={4}>Infrastructure</Title>
                <Text
                    size={'sm'}
                    c={'dimmed'}
                >
                    Loading infrastructure record…
                </Text>
            </Stack>
        );
    }

    // ⚠️ An existing record is DISPLAYED, never duplicated. The entity is
    // append-only per provisioning act; a second row created by an admin who
    // was offered a register button is a second inventory describing the same
    // resources, with no way to tell which one teardown should believe.
    if (hasInventory && record) {
        return <ApplicationInfrastructureProfile record={record}/>;
    }

    if (registering) {
        return (
            <Stack gap={'md'}>
                <Title order={4}>Register existing infrastructure</Title>
                <RegisterInfrastructureForm
                    // Pre-fill from the legacy Application fields so an admin
                    // does not retype what the record already knows. These are
                    // exactly the fields FNP-998's repair form wrote.
                    initialValues={{
                        sourceRepositoryName: (application.sourceRepositoryName as string) ?? '',
                        infraRepositoryName: (application.infraRepositoryName as string) ?? '',
                        sourcePipelineId: (application.sourcePipelineId as string) ?? '',
                        infraPipelineId: (application.infraPipelineId as string) ?? '',
                        deploymentAccountGroupId: (application.deploymentAccountGroupId as string) ?? '',
                    }}
                    handleSubmit={async (values: RegisterInfrastructureFormValues) => {
                        await registerRecord(values);
                        setRegistering(false);
                    }}
                    handleCancel={() => setRegistering(false)}
                />
            </Stack>
        );
    }

    return (
        <Stack gap={'md'}>
            <Title order={4}>Infrastructure</Title>

            <Alert
                variant={'light'}
                color={'blue'}
                icon={<AiOutlineInfoCircle/>}
                title={'No infrastructure recorded for this application'}
            >
                <Text size={'sm'}>
                    Nothing on this platform has recorded what resources back this
                    application. If it is a live application that was created before
                    programmatic provisioning existed, record what is really there so
                    inventory and decommissioning know about it.
                </Text>
                <Text
                    size={'sm'}
                    mt={'xs'}
                >
                    Registering <strong>creates no AWS resources</strong> — it only writes
                    down what already exists, and the resulting record is protected from
                    teardown.
                </Text>
            </Alert>

            <Group>
                <Button
                    leftSection={<AiOutlinePlus/>}
                    onClick={() => setRegistering(true)}
                >
                    Register existing infrastructure
                </Button>
            </Group>
        </Stack>
    );
};
